import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@holigral/shared/ui/components/ui/dialog"
import { Badge } from "@holigral/shared/ui/components/ui/badge"
import { Spinner } from "@holigral/shared/ui/components/ui/spinner"
import StatusBadge from "./StatusBadge"
import { useGetAssignmentQuery } from "./api"
import { useT } from "@/i18n"
import { useCurrentTenant } from "@/tenant"
import { formatDate } from "@/utils"
import type { Uuid } from "@/utils"

type RowProps = {
  readonly label: string
  readonly children: ReactNode
}

function DetailRow({ label, children }: RowProps) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

type Props = {
  readonly assignmentId: Uuid
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

export default function AssignmentDetailDialog({
  assignmentId,
  open,
  onOpenChange,
}: Props) {
  const { t } = useT("assignment")
  const { tenantSlug } = useCurrentTenant()
  const { data: detail } = useGetAssignmentQuery(assignmentId, { skip: !open })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("detail.title")}</DialogTitle>
        </DialogHeader>
        {detail ? (
          <dl className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
            <DetailRow label={t("detail.respondent")}>
              <span className="inline-flex items-center gap-2">
                {detail.assignment.respondent.email ?? "—"}
                {detail.assignment.isSelf && (
                  <Badge variant="outline">{t("detail.selfAssignment")}</Badge>
                )}
              </span>
            </DetailRow>
            <DetailRow label={t("table.status")}>
              <StatusBadge status={detail.assignment.status} />
            </DetailRow>
            <DetailRow label={t("detail.requester")}>
              {detail.assignment.createdBy?.email ?? "—"}
            </DetailRow>
            <DetailRow label={t("detail.language")}>
              {t(`common:locales.${detail.assignment.locale}`, {
                defaultValue: detail.assignment.locale,
              })}
            </DetailRow>
            <DetailRow label={t("detail.brief")}>
              {detail.assignment.brief ? (
                <Link
                  to="/t/$tenantSlug/briefs/$briefId"
                  params={{ tenantSlug, briefId: detail.assignment.brief.id }}
                  className="underline underline-offset-3 hover:text-foreground"
                >
                  {detail.assignment.brief.name}
                </Link>
              ) : (
                "—"
              )}
            </DetailRow>
            <DetailRow label={t("detail.sent")}>
              {formatDate("long", detail.assignment.createdAt)}
            </DetailRow>
            <DetailRow label={t("detail.submitted")}>
              {detail.assignment.submittedAt
                ? formatDate("long", detail.assignment.submittedAt)
                : "—"}
            </DetailRow>
            <DetailRow label={t("detail.lastReminder")}>
              {detail.assignment.lastReminderAt
                ? formatDate("long", detail.assignment.lastReminderAt)
                : "—"}
            </DetailRow>
            {!!detail.assignment.personalMessage && (
              <DetailRow label={t("detail.personalMessage")}>
                <span className="whitespace-pre-wrap">
                  {detail.assignment.personalMessage}
                </span>
              </DetailRow>
            )}
          </dl>
        ) : (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
